import React from 'react'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { IssueLabel } from '../../../../components/entities/IssueLabel'
import { ConvertibleLabel } from '../../../../components/entities/ConvertibleLabel'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined'
import { IssuesGroup } from 'types/IssuesGroupEntity'
import { useFormContext } from 'react-hook-form'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'

export function IssueGroups({
    title,
    issueGroups,
    isLinkedIssues,
    handleDeleteFromGroup,
    handleAddToGroup,
    isGuarantorFeeIssue,
}: {
    handleAddToGroup: (groupId: string) => void
    handleDeleteFromGroup: (groupId: string) => void
    isLinkedIssues?: boolean
    title: string
    issueGroups: Array<IssuesGroup>
    isGuarantorFeeIssue: boolean
}) {
    const { watch } = useFormContext<FormIssueEntity | FormConvertibleEntity>()
    const entityId = watch('id')
    const linkedIssues = watch('linkedIssues')

    return (
        <>
            <Typography variant='subtitle1'>
                <strong>{title}</strong>
            </Typography>
            <List>
                {issueGroups
                    ?.sort((a, b) => a.groupId.localeCompare(b.groupId)) // Sort to avoid flickering upon add/delete
                    .filter((ig) => {
                        const entitesCount = [
                            ...ig.issues.map((i) => i.id),
                            ...ig.convertibles.map((c) => c.id),
                        ].filter((id) => id !== entityId).length

                        if (!isLinkedIssues && entitesCount < 2) return false // This issue group is a singleton and will be removed upon save
                        if (!isLinkedIssues && linkedIssues.some((l) => l.groupId === ig.groupId)) return false // Don't show groups that exist in linkedIssues
                        if (!isGuarantorFeeIssue || isLinkedIssues) return true // Always show all groups for non-guarantor fee issues and for linked issues

                        return ig.issues.some((e) => e.id === entityId)
                    })
                    .map((ig) => {
                        return (
                            <ListItem key={ig.groupId} sx={{ border: 'dotted 1px', borderRadius: 3 }}>
                                <Stack direction='column' spacing={1}>
                                    {!isLinkedIssues && (
                                        <Tooltip title='Lägg till i gruppen'>
                                            <IconButton onClick={() => handleAddToGroup(ig.groupId)}>
                                                <AddCircleOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {isLinkedIssues && (
                                        <Tooltip title='Ta bort från gruppen'>
                                            <IconButton onClick={() => handleDeleteFromGroup(ig.groupId)}>
                                                <RemoveCircleOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                                <List>
                                    {ig.issues
                                        .filter((i) => i.id !== entityId)
                                        .map((issue) => (
                                            <ListItem key={`${ig.groupId}_${issue.id}`}>
                                                <Typography variant='body2'>
                                                    <IssueLabel adminLink issue={issue} />{' '}
                                                </Typography>
                                            </ListItem>
                                        ))}
                                    {ig.convertibles
                                        .filter((c) => c.id !== entityId)
                                        .map((convertible) => (
                                            <ListItem key={`${ig.groupId}_${convertible.id}`}>
                                                <Typography variant='body2'>
                                                    <ConvertibleLabel
                                                        adminLink
                                                        convertible={{ ...convertible, id: convertible.id }}
                                                    />
                                                </Typography>
                                            </ListItem>
                                        ))}
                                </List>
                            </ListItem>
                        )
                    })}
            </List>
        </>
    )
}
